import { IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import parse, { Text } from 'html-react-parser';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { AnswerSource } from '@cfa/pathway-fe-shared/xp';

import { fixTridionImageSources } from '@/utils/document-utils';

import '@/styles/document.css';

import { extractHtmlSnippet } from '../extractHtmlSnippet';
import { previewingSourceAtom } from '../state';

import SmartSourceCard from './SmartSourceCard';

/**
 * Highlights occurrences of matchedText within a text string.
 * Returns an array of React nodes with matched portions wrapped in <mark>.
 *
 * Dropped for now because of inconsistencies in the highlighting
 */
// const highlightText = (
//   text: string,
//   matchedText: string,
// ): React.ReactNode[] => {
//   if (!matchedText || !text) {
//     return [text];
//   }

//   // Escape special regex characters in the matched text
//   const escaped = matchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
//   const regex = new RegExp(`(${escaped})`, 'gi');
//   const parts = text.split(regex);

//   return parts.map((part, index) =>
//     part.toLowerCase() === matchedText.toLowerCase() ? (
//       <mark
//         key={index}
//         className="bg-brand-navy/20 rounded-sm px-0.5 text-inherit"
//       >
//         {part}
//       </mark>
//     ) : (
//       part
//     ),
//   );
// };

/** Parses HTML and highlights occurrences of matchedText within text nodes. */
const parseWithHighlighting = (
  html: string,
  matchedText: string,
): React.ReactNode =>
  parse(html, {
    replace: domNode => {
      // Process text nodes to inject highlighting
      if (domNode instanceof Text) {
        const text = domNode.data;
        if (text && matchedText) {
          const lowerText = text.toLowerCase();
          const lowerMatch = matchedText.toLowerCase();
          // Only process if this text contains the match
          if (lowerText.includes(lowerMatch)) {
            return <>{text}</>;
          }
        }
      }
      // Let elements be processed normally (their children will be processed recursively)
      return undefined;
    },
  });

interface SourcePreviewProps {
  source: AnswerSource;
  matchedText: string;
}

const SourcePreview: React.FC<SourcePreviewProps> = ({
  source,
  matchedText,
}) => {
  const { t } = useTranslation();
  const setPreviewingSource = useSetAtom(previewingSourceAtom);

  const { data: htmlContent } = useQuery({
    queryKey: ['source-preview-html', source.contentUrl],
    queryFn: () =>
      fetch(source.contentUrl, { credentials: 'include' }).then(res =>
        res.text(),
      ),
  });

  // Extract HTML snippet around the matched text, fallback to null if not found
  const htmlSnippet =
    htmlContent && matchedText
      ? extractHtmlSnippet(htmlContent, matchedText)
      : null;

  const fixedSnippet = fixTridionImageSources(htmlSnippet ?? undefined);

  return (
    <div className="bg-th-bg flex h-min flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-variant-body1 text-grayscale-gray3 font-medium uppercase">
          {t('Generic.reference')}
        </p>
        <button
          className="bg-th-bg cursor-pointer rounded-full p-1! transition-colors hover:bg-[#000000]/15"
          onClick={() => setPreviewingSource(undefined)}
        >
          <IconX size={24} className="text-grayscale-gray6" />
        </button>
      </div>
      {fixedSnippet && (
        <div className="text-variant-body2 tridionDocument mb-2 w-full! overflow-hidden! bg-transparent! p-0!">
          {parseWithHighlighting(fixedSnippet, matchedText)}
        </div>
      )}
      <SmartSourceCard source={source} />
    </div>
  );
};

export default SourcePreview;
